import { useState, type ComponentProps } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v4'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Eye, EyeOff } from 'lucide-react'
import { toast } from 'sonner'
import { useClerk, useUser } from '@clerk/clerk-react'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useRateLimitCountdown } from '@/hooks/useRateLimitCountdown'
import { changePassword, fetchHasPassword, signOutEverywhere, updateProfile } from '@/api/users'
import { Avatar } from '@/components/Avatar'
import { AVATAR_PRESETS } from '@/lib/avatarPresets'
import { MFA_ENABLED } from '@/lib/featureFlags'
import { signOutAndReset } from '@/lib/signOut'
import { ROUTES } from '@/routes/routes'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet } from '@/components/ui/sheet'
import { SettingsPanel, SettingsRow, ROW_BUTTON } from './SettingsPanel'

const nameSchema = z.object({
  displayName: z.string().min(1, 'Display name is required').max(60, 'Max 60 characters'),
})

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Required'),
  newPassword: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[\d\W]/, 'Password must contain at least one digit or symbol'),
})

const signOutSchema = z.object({
  currentPassword: z.string().min(1, 'Required'),
})

type Open = 'name' | 'avatar' | 'password' | 'signOut' | null

export function YouPanel() {
  const { data: user } = useCurrentUser()
  const passwordStatus = useQuery({
    queryKey: ['currentUser', 'hasPassword'],
    queryFn: fetchHasPassword,
    retry: false,
  })
  // The web app deploys minutes before the API: until the endpoint exists, /users/me still carries it.
  const hasPassword = passwordStatus.isError ? user?.hasPassword : passwordStatus.data
  const { user: clerkUser } = useUser()
  const [open, setOpen] = useState<Open>(null)
  const close = () => setOpen(null)

  return (
    <SettingsPanel
      id="you"
      title="You"
      overlays={
        <>
          {open === 'name' && <NameSheet onClose={close} />}
          {open === 'avatar' && <AvatarSheet onClose={close} />}
          {open === 'password' && <PasswordSheet onClose={close} />}
          {open === 'signOut' && <SignOutSheet onClose={close} />}
        </>
      }
    >
      <SettingsRow
        label={
          <span className="flex items-center gap-3">
            <Avatar avatarPreset={user?.avatarPreset ?? 'avatar-coral'} size="sm" />
            <span className="min-w-0">
              <span className="block truncate">{user?.displayName}</span>
              <span className="block truncate text-xs font-normal text-[var(--fl-text-2)]">
                {user?.email}
              </span>
            </span>
          </span>
        }
      >
        <Button variant="outline" className={ROW_BUTTON} onClick={() => setOpen('name')}>
          Edit name
        </Button>
      </SettingsRow>

      <SettingsRow label="Avatar" hint="How you appear to the rest of your household">
        <Button variant="outline" className={ROW_BUTTON} onClick={() => setOpen('avatar')}>
          Change
        </Button>
      </SettingsRow>

      <SettingsRow label="Appearance" hint="On this device" stack>
        <ThemeToggle />
      </SettingsRow>

      {hasPassword !== undefined && (
        <SettingsRow
          label="Password"
          // Google is currently the app's only OAuth provider — nothing to detect/guess.
          hint={hasPassword ? undefined : 'You signed in with Google. Manage your password there.'}
        >
          {hasPassword && (
            <Button variant="outline" className={ROW_BUTTON} onClick={() => setOpen('password')}>
              Change password
            </Button>
          )}
        </SettingsRow>
      )}

      {/* Hidden while MFA_ENABLED is false; see that file for why. */}
      {MFA_ENABLED && (
        <SettingsRow
          label="Two-factor sign-in"
          hint={
            clerkUser?.totpEnabled
              ? "You'll be asked for an authenticator code on every sign-in."
              : 'Ask for a code from an authenticator app when signing in.'
          }
          value={clerkUser?.totpEnabled ? 'On' : 'Off'}
        >
          <Button variant="outline" asChild className={ROW_BUTTON}>
            <Link to={clerkUser?.totpEnabled ? ROUTES.APP.DISABLE_2FA : ROUTES.APP.ENABLE_2FA}>
              {clerkUser?.totpEnabled ? 'Turn off' : 'Turn on'}
            </Link>
          </Button>
        </SettingsRow>
      )}

      <SettingsRow label="Sessions" hint="Sign out of every device, including this one">
        <Button variant="outline" className={ROW_BUTTON} onClick={() => setOpen('signOut')}>
          Sign out everywhere
        </Button>
      </SettingsRow>
    </SettingsPanel>
  )
}

function PasswordInput(props: ComponentProps<typeof Input>) {
  const [shown, setShown] = useState(false)
  return (
    <div className="relative">
      <Input {...props} type={shown ? 'text' : 'password'} className="pr-10" />
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() => setShown((v) => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--fl-text-2)]"
        tooltip={shown ? 'Hide password' : 'Show password'}
      >
        {shown ? <EyeOff size={16} /> : <Eye size={16} />}
      </Button>
    </div>
  )
}

function FieldError({ message }: { message?: string }) {
  return message ? <p className="mt-1 text-xs text-[var(--fl-error)]">{message}</p> : null
}

function NameSheet({ onClose }: { onClose: () => void }) {
  const { data: user } = useCurrentUser()
  const queryClient = useQueryClient()
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<z.infer<typeof nameSchema>>({
    resolver: zodResolver(nameSchema),
    defaultValues: { displayName: user?.displayName ?? '' },
  })

  async function onSubmit(values: z.infer<typeof nameSchema>) {
    try {
      const updated = await updateProfile({ displayName: values.displayName })
      queryClient.setQueryData(['currentUser'], updated)
      toast.success('Profile updated.')
      onClose()
    } catch {
      toast.error('Something went wrong. Please try again.')
    }
  }

  return (
    <Sheet title="Your name" onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label
            htmlFor="displayName"
            className="mb-1 block text-sm font-medium text-[var(--fl-text-1)]"
          >
            Display name
          </label>
          <Input id="displayName" maxLength={60} autoFocus {...register('displayName')} />
          <FieldError message={errors.displayName?.message} />
        </div>
        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium text-[var(--fl-text-1)]">
            Email
          </label>
          <Input
            id="email"
            type="email"
            value={user?.email ?? ''}
            readOnly
            className="bg-[var(--fl-surface-hover)] text-[var(--fl-text-2)]"
          />
          <p className="mt-1 text-xs text-[var(--fl-text-2)]">Email cannot be changed.</p>
        </div>
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!isDirty || isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Sheet>
  )
}

function AvatarSheet({ onClose }: { onClose: () => void }) {
  const { data: user } = useCurrentUser()
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (avatarPreset: string) => updateProfile({ avatarPreset }),
    onSuccess: (updated) => queryClient.setQueryData(['currentUser'], updated),
    onError: () => toast.error('Something went wrong. Please try again.'),
  })

  return (
    <Sheet title="Avatar" onClose={onClose}>
      <p className="mb-4 text-sm text-[var(--fl-text-2)]">
        Pick how you appear to the rest of your household.
      </p>
      <div className="flex flex-wrap gap-3" role="group" aria-label="Avatar options">
        {AVATAR_PRESETS.map((preset) => {
          const selected = user?.avatarPreset === preset
          return (
            <button
              key={preset}
              type="button"
              onClick={() => mutation.mutate(preset)}
              disabled={mutation.isPending}
              aria-label={preset.replace('avatar-', '')}
              aria-pressed={selected}
              className={[
                'rounded-full p-0.5 transition-shadow disabled:opacity-50',
                selected
                  ? 'ring-2 ring-[var(--fl-primary)] ring-offset-2 ring-offset-[var(--fl-surface)]'
                  : 'hover:ring-2 hover:ring-[var(--fl-border)] hover:ring-offset-2 hover:ring-offset-[var(--fl-surface)]',
              ].join(' ')}
            >
              <Avatar avatarPreset={preset} size="sm" />
            </button>
          )
        })}
      </div>
      <div className="mt-5 flex justify-end">
        <Button type="button" variant="outline" onClick={onClose}>
          Done
        </Button>
      </div>
    </Sheet>
  )
}

function PasswordSheet({ onClose }: { onClose: () => void }) {
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const { secondsLeft, message: rateLimitMessage, handleRateLimit } = useRateLimitCountdown()
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof passwordSchema>>({ resolver: zodResolver(passwordSchema) })

  async function onSubmit(values: z.infer<typeof passwordSchema>) {
    setPasswordError(null)
    try {
      await changePassword(values.currentPassword, values.newPassword)
      toast.success("Password updated. We've signed you out of all other devices.")
      onClose()
    } catch (err: unknown) {
      if (handleRateLimit(err)) return
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setPasswordError(
        detail === 'WRONG_CURRENT_PASSWORD'
          ? 'Current password is incorrect.'
          : 'Something went wrong. Please try again.'
      )
    }
  }

  return (
    <Sheet title="Change password" onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {(passwordError || rateLimitMessage) && (
          <div className="rounded-lg bg-[var(--fl-error-bg)] px-4 py-3 text-sm text-[var(--fl-error)]">
            {rateLimitMessage ?? passwordError}
          </div>
        )}
        <div>
          <label
            htmlFor="currentPassword"
            className="mb-1 block text-sm font-medium text-[var(--fl-text-1)]"
          >
            Current password
          </label>
          <PasswordInput id="currentPassword" {...register('currentPassword')} />
          <FieldError message={errors.currentPassword?.message} />
        </div>
        <div>
          <label
            htmlFor="newPassword"
            className="mb-1 block text-sm font-medium text-[var(--fl-text-1)]"
          >
            New password
          </label>
          <PasswordInput id="newPassword" {...register('newPassword')} />
          <FieldError message={errors.newPassword?.message} />
        </div>
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || secondsLeft !== null}>
            {secondsLeft !== null
              ? `Try again in ${secondsLeft}s`
              : isSubmitting
                ? 'Changing…'
                : 'Change password'}
          </Button>
        </div>
      </form>
    </Sheet>
  )
}

function SignOutSheet({ onClose }: { onClose: () => void }) {
  const clerk = useClerk()
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof signOutSchema>>({ resolver: zodResolver(signOutSchema) })

  async function onSubmit(values: z.infer<typeof signOutSchema>) {
    try {
      await signOutEverywhere(values.currentPassword)
      onClose()
      await signOutAndReset(clerk, ROUTES.AUTH.SIGN_IN)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError('currentPassword', {
        message:
          detail === 'WRONG_CURRENT_PASSWORD'
            ? 'Incorrect password.'
            : 'Something went wrong. Please try again.',
      })
    }
  }

  return (
    <Sheet title="Sign out of all devices?" onClose={onClose}>
      <p className="mb-4 text-sm text-[var(--fl-text-2)]">
        This ends every session everywhere, including this device. You'll need to sign in again.
      </p>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label
            htmlFor="signOutPassword"
            className="mb-1 block text-sm font-medium text-[var(--fl-text-1)]"
          >
            Current password
          </label>
          <PasswordInput id="signOutPassword" {...register('currentPassword')} />
          <FieldError message={errors.currentPassword?.message} />
        </div>
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="destructive" disabled={isSubmitting}>
            {isSubmitting ? 'Signing out…' : 'Sign out'}
          </Button>
        </div>
      </form>
    </Sheet>
  )
}
